import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { ProjectCard } from "../components/ProjectCard";
import { loadProjects } from "../lib/projects";

const projects = loadProjects();
const desktopProjectRows = Array.from(
  { length: Math.ceil(projects.length / 3) },
  (_, rowIndex) => projects.slice(rowIndex * 3, rowIndex * 3 + 3)
);

export function ProjectsPage() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const projectRefs = useRef<(HTMLElement | null)[]>([]);
  const desktopRowRefs = useRef<(HTMLElement | null)[]>([]);
  const [projectProgress, setProjectProgress] = useState<number[]>(() => projects.map(() => 0));
  const [desktopRowProgress, setDesktopRowProgress] = useState<number[]>(() => desktopProjectRows.map(() => 0));

  const updateProgress = useCallback(() => {
    const viewport = window.innerHeight;

    const newDesktopRowProgress = desktopProjectRows.map((_, i) => {
      const el = desktopRowRefs.current[i];
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const range = el.offsetHeight - viewport;
      return range > 0 ? Math.max(0, Math.min(1, -rect.top / range)) : 0;
    });
    setDesktopRowProgress(newDesktopRowProgress);

    const newProgress = projects.map((_, i) => {
      const el = projectRefs.current[i];
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const range = el.offsetHeight - viewport;
      return range > 0 ? Math.max(0, Math.min(1, -rect.top / range)) : 0;
    });
    setProjectProgress(newProgress);
  }, [scrollProgress]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenis.on("scroll", (e: { scroll: number }) => setScrollProgress(e.scroll));
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  useEffect(() => {
    const ro = new ResizeObserver(updateProgress);
    desktopRowRefs.current.forEach((rowRef) => {
      if (rowRef) ro.observe(rowRef);
    });
    projectRefs.current.forEach((projectRef) => {
      if (projectRef) ro.observe(projectRef);
    });
    return () => ro.disconnect();
  }, [updateProgress]);

  return (
    <div className="app">
      <div className="bg-layer" aria-hidden="true" />
      <main id="main-content" tabIndex={-1}>
        <section className="projects p-0">
          <div className="safe-gutter pt-4 pb-6 max-w-[1200px] mx-auto md:pt-6 md:pb-2">
            <Link to="/#projects" className="font-mono text-[0.7rem] tracking-[0.2em] text-accent mb-4 block hover:underline w-fit">
              ← Back
            </Link>
            <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Portfolio</span>
            <h1 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-0">
              All Projects
            </h1>
          </div>

          <div className="hidden md:block">
            {desktopProjectRows.map((rowProjects, rowIndex) => (
              <section
                key={rowProjects.map((project) => project.id).join("-")}
                ref={(el) => { desktopRowRefs.current[rowIndex] = el; }}
                className="min-h-[200vh] p-0"
              >
                <div className="sticky top-0 screen-stage flex flex-col justify-center safe-gutter">
                  <div className="max-w-[1200px] mx-auto w-full">
                    <div className="grid grid-cols-3 gap-2">
                      {rowProjects.map((project, projectIndex) => {
                        const index = rowIndex * 3 + projectIndex;
                        return (
                          <Link key={project.id} to={`/projects/${project.id}`} state={{ from: "projects" }} className="block">
                            <ProjectCard project={project} onClick={() => {}} index={index} scrollProgress={desktopRowProgress[rowIndex] ?? 0} />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>

          <div className="max-w-[1200px] mx-auto block md:hidden">
            {projects.map((project, i) => (
              <section
                key={project.id}
                ref={(el) => { projectRefs.current[i] = el; }}
                className="min-h-[200vh] p-0"
              >
                <div className="sticky top-0 screen-stage flex flex-col justify-center safe-gutter">
                  <Link to={`/projects/${project.id}`} state={{ from: "projects" }} className="block w-full max-w-[400px] mx-auto">
                    <ProjectCard project={project} onClick={() => {}} index={i} scrollProgress={projectProgress[i] ?? 0} />
                  </Link>
                </div>
              </section>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
